import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Icon } from "@iconify/react";

export default function AddPromotion({ open, onClose, eventId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(false);
    const modalRef = useRef(null);
    const [images, setImages] = useState([]);

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);

        // Create preview objects
        const newImages = files.map(file => ({
            file,
            preview: URL.createObjectURL(file),
        }));

        // Combine with existing previews
        const updatedImages = [...images, ...newImages];

        // Update local image state
        setImages(updatedImages);

        // Pass only the raw file objects to Formik
        const fileArray = updatedImages.map((img) => img.file);
        newPromotion.setFieldValue('images', fileArray);
    };


    const handleRemoveImage = (index) => {
        const updatedImages = images.filter((_, i) => i !== index);
        setImages(updatedImages);

        // Update Formik with remaining files
        const updatedFiles = updatedImages.map(img => img.file);
        newPromotion.setFieldValue('images', updatedFiles);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const handleClose = () => {
        onClose();
        newPromotion.resetForm();
    };

    const onSubmit = async (values) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('isActive', values.isActive);
            formData.append('eventId', eventId);
            values.images.forEach((file) => {
                formData.append('images', file, file.name);
            })
            await axios.post(`${apiUrl}event-service/promoted-event`, formData, {
                headers: {
                    'Authorization': `Bearer ${bearerToken}`,
                    'Content-Type': 'multipart/form-data',
                    'accept': 'application/json',
                },
            });
            toast.success('Event has been promoted!');
        } catch (error) {
            console.error('Error inserting data:', error.message);
            toast.error('Unable to promote the event.' || error.message);
        } finally {
            setIsLoading(false);
            setTimeout(() => {
                handleClose();
            }, 1000);
        }
    };

    const newPromotion = useFormik({
        initialValues: {
            images: null,
            isActive: true,
        },
        validationSchema: Yup.object({
            isActive: Yup.string().required('Status is required'),
            images: Yup.array()
                .required()
                .max(5, "You can only upload up to 5 images")
                .test("validImages", "Invalid image(s) in array", (value) => {
                    if (!value) return true; // Not required
                    return value.every(
                        (img) =>
                            typeof img === "object" ||
                            (typeof img === "string" && img.startsWith("http"))
                    );
                })
                .test("fileSize", "Each file must be less than 5MB", (value) => {
                    if (!value) return true; // Not required
                    return value.every((img) => {
                        if (typeof img === "string") return true; // Existing URL
                        return img.size <= 5 * 1024 * 1024;
                    });
                }),
        }),
        onSubmit,
    });

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black opacity-50" onClick={onClose}></div>

            {/* Modal */}
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative">
                {/* Header */}
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-xl font-semibold text-center">Add Event Promotions</h2>
                </div>

                <div className="relative bg-white p-6 rounded-b-lg shadow-md">
                    <form onSubmit={newPromotion.handleSubmit} className="space-y-6">
                        <div className="w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Images
                            </label>
                            <div className="bg-gray-50 p-4 rounded-md">
                                <p className="text-xs text-gray-400 mb-2 italic">Please upload images, size less than 5MB each, upto 5 images</p>
                                <div className="flex items-center">
                                    <label className="flex items-center text-sm font-medium px-4 py-2 text-blue-500 bg-blue-50 hover:bg-blue-200 rounded-lg cursor-pointer">
                                        {/* <ImageIcon size={20} className="mr-2" /> */}
                                        Upload Images
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            className="hidden"
                                            required
                                        />
                                    </label>
                                </div>

                                {/* Image Preview */}
                                <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-4">
                                    {images.map((image, index) => (
                                        <div key={index} className="relative">
                                            <img
                                                src={image.preview}
                                                alt={`Venue ${index + 1}`}
                                                className="w-full h-32 object-cover rounded-md"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                            >
                                                <Icon icon="carbon:close-outline" className="sm" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {newPromotion.touched.images && newPromotion.errors.images && (
                                <p className="text-red-500 text-sm mt-1">{newPromotion.errors.images}</p>
                            )}
                        </div>

                        <div className="w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                            </label>
                            <select
                                name="isActive"
                                value={newPromotion.values.isActive}
                                onChange={(e) => {
                                    newPromotion.setFieldValue("isActive", e.target.value === "true");
                                }}
                                onBlur={newPromotion.handleBlur}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                            {newPromotion.touched.isActive && newPromotion.errors.isActive && (
                                <div className="text-red-500 text-xs mt-1">{newPromotion.errors.isActive}</div>
                            )}
                        </div>

                        <div className="flex justify-end mt-4">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>

            </div>

            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
}

AddPromotion.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    eventId: PropTypes.string.isRequired,
};